import {
    Pagination,
    styled,
    Table,
    TableBody,
    TableCell as MuiTableCell,
    TableContainer,
    TableHead,
    TableRow as MuiTableRow,
    Typography
} from '@mui/material';
import { CaseHoursPastCyclesDto } from '../../../lib/dtos/CaseHoursPastCyclesDto';
import useGetPaginatedData from '../../../lib/hooks/use-pagination-data';
import theme from '../../../theme';
import { useScreenLargerThan } from '../../../lib/hooks/breakpoint-hooks';
import Link from '../../Utils/Link';

export type PastCyclesCaseHoursTableProps = {
    invoiceCycleId: string;
};

const PastCyclesCaseHoursTable = ({ invoiceCycleId }: PastCyclesCaseHoursTableProps) => {
    const isLargerThanMedium = useScreenLargerThan('md');
    const { data, paginationHandler } = useGetPaginatedData<CaseHoursPastCyclesDto>(
        `completed-review/invoice-cycle/${invoiceCycleId}`
    );
    const TableCellMobile = styled(TableCell)({
        backgroundColor: theme.palette.listItemBackground.shade,
        width: '35%',
        paddingRight: '0',
        paddingLeft: '2%'
    });

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    {isLargerThanMedium && (
                        <TableRow>
                            <TableCell
                                sx={{ backgroundColor: (t) => t.palette.listItemBackground.shade }}>
                                <Typography fontSize={14} fontWeight="700">
                                    Case Hours
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    fontSize={13}
                                    color={(t) => t.palette.cardSubtitle.main}
                                    fontWeight={'600'}>
                                    Case Number
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    fontSize={13}
                                    color={(t) => t.palette.cardSubtitle.main}
                                    fontWeight={'600'}>
                                    Case Stage
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    fontSize={13}
                                    color={(t) => t.palette.cardSubtitle.main}
                                    fontWeight={'600'}>
                                    Review Minutes
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    fontSize={13}
                                    color={(t) => t.palette.cardSubtitle.main}
                                    fontWeight={'600'}>
                                    Case Category
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}

                    {!isLargerThanMedium && (
                        <TableRow>
                            <TableCellMobile sx={{ width: '35%' }}>
                                <Typography fontSize={14} fontWeight="700">
                                    Case Hours
                                </Typography>
                            </TableCellMobile>
                            <TableCell
                                sx={{
                                    backgroundColor: (t) => t.palette.listItemBackground.shade
                                }}></TableCell>
                        </TableRow>
                    )}
                </TableHead>
                {isLargerThanMedium && data && (
                    <TableBody>
                        {data.data.map((ch, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {new Date(ch.date).toLocaleDateString()}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 'bold'
                                        }}>
                                        {ch.clinicalReviewId && ch.crmId ? (
                                            <Link
                                                sx={{ textDecoration: 'none' }}
                                                to={`/completedCaseDetails/${ch.clinicalReviewId}/${ch.crmId}`}>
                                                {ch.caseNumber}
                                            </Link>
                                        ) : (
                                            ch.caseNumber
                                        )}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {ch.caseStage}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {ch.reviewedMinutes} Minutes
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {ch.caseCategory}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                )}

                {!isLargerThanMedium && data && (
                    <TableBody>
                        {data.data.map((ch, index) => (
                            <TableRow key={index}>
                                <TableCell sx={{ paddingRight: '1%', paddingLeft: '2%' }}>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {new Date(ch.date).toLocaleDateString()}
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {ch.reviewedMinutes} Minutes
                                    </Typography>
                                </TableCell>

                                <TableCell sx={{ paddingRight: '1%', paddingLeft: '2%' }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>
                                        {ch.clinicalReviewId && ch.crmId ? (
                                            <Link
                                                sx={{ textDecoration: 'none' }}
                                                to={`/completedCaseDetails/${ch.clinicalReviewId}/${ch.crmId}`}>
                                                {ch.caseNumber}
                                            </Link>
                                        ) : (
                                            ch.caseNumber
                                        )}
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {ch.caseStage}
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={'600'}>
                                        {ch.caseCategory}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                )}
            </Table>
            {data && (
                <Pagination
                    shape="rounded"
                    page={data.page}
                    count={data.totalPages}
                    onChange={paginationHandler}
                />
            )}
        </TableContainer>
    );
};

const TableCell = styled(MuiTableCell)(() => ({
    borderWidth: 2,
    borderColor: 'white'
}));

const TableRow = styled(MuiTableRow)(({ theme: t }) => ({
    backgroundColor: t.palette.listItemBackground.shade
}));

export default PastCyclesCaseHoursTable;
